import React, {createContext, MutableRefObject, useEffect, useRef, useState} from "react";
import {HybridQuestion, Question, QuestionAttempt, QuestionGroup, RawQuestion} from "../models/question";
import {useAsync} from "../hooks/useAsyncResult";
import {submitSession} from "../services/api/session";
import {Test} from "../models/test";
import {defaultTestTime} from "../utils/defaultTestTime";
import {SectionName} from "../models/section";


export type TestRoomContextState = {
    questions?: (Question | QuestionGroup)[],
    setQuestions?: (questions: (Question | QuestionGroup)[]) => void,
    questionAttempts?: QuestionAttempt[],
    currentSubsectionNumber?: number,
    currentSubsectionQuestionNumber?: number,
    answerQuestion?: (userAnswer: QuestionAttempt['userAnswer'], question: RawQuestion | Question, questionInfo?: {
        sectionName: SectionName,
        questionId: string,
        isFromGroup: boolean,
        testId: string,
        questionGroupIndex: number,
        contentType: "image" | "text" | "audio"
    }) => void,
    nextSubsection?: () => void,
    previousSubsection?: () => void,
    flagQuestion?: () => void,
    flaggedQuestions?: string[],
    submitting?: boolean,
    submitFailed?: boolean,
    timerEnabled?: boolean,
    setTimerEnabled?: (value: boolean) => void,
    awaitTimer?: (time: number, onTimeEnd: () => Promise<void>) => void,
    testSubmitted?: boolean,
    hasRequestedSubmit?: boolean,
    sessionId?: string,
    setSessionId?: (sessionId: string) => void,
    submit?: (questionAttempt?: QuestionAttempt[], timeout?: boolean) => void,
    setTest?: (test: Test) => void,
    test?: Test,
    cursorPositionRef?: MutableRefObject<number>,
    clearTimeouts?: () => void,
    answeredQuestions?: number,
    setAnsweredQuestions?: (answeredQuestions: number) => void,
    totalQuestions?: MutableRefObject<number>,
    canGoToNextSubsection?: boolean,
    canGoToPreviousSubsection?: boolean,
    canNavigateSubsections?: boolean,
    reachedSubsectionsRef?: MutableRefObject<number[]>
}

export const ITestRoomContext = createContext<TestRoomContextState>({})


const TestRoomContext = (props: { children }) => {
    const [sessionId, setSessionId] = useState<string>()
    const [test, setTest] = useState<Test>()
    const [questions, setQuestions] = useState<(Question | QuestionGroup)[]>([])
    const [currentSubsectionNumber, setCurrentSubsectionNumber] = useState<number>(1)
    const [currentSubsectionQuestionNumber, setCurrentSubsectionQuestionNumber] = useState<number>(null)
    const [questionAttempts, setQuestionAttempts] = useState<QuestionAttempt[]>([])
    const [canGoToNextSubsection, setCanGoToNextSubsection] = useState(false)
    const [canGoToPreviousSubsection, setCanGoToPreviousSubsection] = useState(false)
    const [canNavigateSubsections, setCanNavigateSubsections] = useState(false)
    const [flaggedQuestions, setFlaggedQuestions] = useState<string[]>([])
    const [timerEnabled, setTimerEnabled] = useState(false)
    const [answeredQuestions, setAnsweredQuestions] = useState<number>(0)
    const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([])
    const timeTakenRef = useRef<number>(0)
    const totalQuestions = useRef<number>(0)
    const maxSubsectionNumberRef = useRef<number>(1)
    const reachedSubsectionsRef = useRef<number[]>([])
    const cursorPositionRef = useRef(0)
    const hasRequestedSubmitRef = useRef(false)
    const submitSessionAsyncResult = useAsync(submitSession, true, true, false)

    const handleAnswerQuestion = (answerValue: QuestionAttempt['userAnswer'], question?: RawQuestion | Question, questionInfo?: {
        sectionName: SectionName,
        questionId: string,
        isFromGroup: boolean,
        testId: string,
        questionGroupIndex: number,
        contentType: "image" | "text" | "audio"
    }) => {
        setCurrentSubsectionQuestionNumber(questionInfo.questionGroupIndex)
        setQuestionAttempts(prevState => {
            const questionAttemptsSlice = prevState.slice()
            const questionAttemptIndex = questionAttemptsSlice.findIndex(value => {
                if (questionInfo.isFromGroup)
                    return value.questionId === questionInfo.questionId && value.questionGroupIndex === questionInfo.questionGroupIndex
                else
                    return value.questionId === questionInfo.questionId
            })
            const questionAttempt: QuestionAttempt = {
                questionId: questionInfo.questionId,
                questionSnapshot: {
                    content: question.content,
                    correctAnswer: question.correctAnswer,
                    sectionName: questionInfo.sectionName,
                    type: question.type,
                    answerType: question.answerType,
                    testId: questionInfo.testId,
                    contentType: questionInfo.contentType,
                },
                userAnswer: answerValue,
                questionGroupIndex: questionInfo.questionGroupIndex,
                timeTakenSecs: timeTakenRef.current,
                answeredAt: new Date().getTime()
            }

            if (questionAttemptIndex > -1)
                questionAttemptsSlice[questionAttemptIndex] = questionAttempt
            else
                questionAttemptsSlice.push(questionAttempt)

            return questionAttemptsSlice
        })
    }

    const handleNextSubsection = () => {
        setCurrentSubsectionNumber(Math.min(maxSubsectionNumberRef.current, currentSubsectionNumber + 1))
        clearTimeouts()
        setCurrentSubsectionQuestionNumber(null)
    }

    const handlePreviousSubsection = () => {
        setCurrentSubsectionNumber(Math.max(1, currentSubsectionNumber - 1))
        clearTimeouts()
        setCurrentSubsectionQuestionNumber(null)
    }

    const handleFlagQuestion = (questionId?: string) => {
        setFlaggedQuestions(prevState => {
            const flaggedQuestionsSlice = prevState.slice()
            const flaggedQuestionIndex = flaggedQuestionsSlice.findIndex(value => value === questionId)
            if (flaggedQuestionIndex > -1)
                flaggedQuestionsSlice.splice(flaggedQuestionIndex)
            else
                flaggedQuestionsSlice.push(questionId)

            return flaggedQuestionsSlice
        })
    }

    const handleSubmit = (qAttempts?: QuestionAttempt[], timeout?: boolean) => {
        let qAs
        if (timeout)
            qAs = fillUnAnsweredQuestions()
        else
            qAs = (qAttempts || questionAttempts).map(value => ({...value, sessionId}))

        hasRequestedSubmitRef.current = true
        submitSessionAsyncResult.execute({sessionId, questionAttempts: qAs})
    }

    const fillUnAnsweredQuestions = () => {
        const result = questionAttempts.slice()
        questions.forEach((question) => {

            if ((question as QuestionGroup).questions)
                (question['questions'] as RawQuestion[]).forEach((rawQuestion, index) => {
                    if (!result.find(value => value.questionId === question.id && value.questionGroupIndex === index))
                        result.push({
                            answeredAt: new Date().getTime(),
                            userAnswer: "",
                            questionId: question.id,
                            questionGroupIndex: index,
                            questionSnapshot: {
                                content: question.content,
                                correctAnswer: rawQuestion.correctAnswer,
                                sectionName: question.sectionName,
                                type: rawQuestion.type,
                                answerType: rawQuestion.answerType,
                                testId: question.testId,
                                contentType: question.contentType,
                            }
                        })
                })

            else if (!questionAttempts.map(value => value.questionId).includes(question.id))
                result.push({
                    answeredAt: new Date().getTime(),
                    userAnswer: "",
                    questionId: question.id,
                    questionSnapshot: {
                        content: question.content,
                        correctAnswer: (question as Question).correctAnswer,
                        sectionName: question.sectionName,
                        type: (question as Question).type,
                        answerType: (question as Question).answerType,
                        testId: question.testId,
                        contentType: question.contentType,
                    }
                })
        })
        return result
    }

    const awaitTimer = (time: number, onTimeEnd: () => Promise<void>) => {
        timeoutIdsRef.current.push(setTimeout(onTimeEnd, time * 1000))
    }

    const clearTimeouts = () => {
        for (let i = 0; i < timeoutIdsRef.current?.length; i++)
            clearTimeout(timeoutIdsRef.current[i])
    }

    const countQuestions = () => {
        let count = 0
        for (let i = 0; i < questions.length; i++) {
            const question = questions[i]
            if ((question as QuestionGroup).questions)
                count += (question as QuestionGroup).questions.length
            else
                count += 1
        }
        totalQuestions.current = count
    }

    const countSubsections = () => {
        questions.forEach((question) => {
            if (question.subSectionNumber > maxSubsectionNumberRef.current)
                maxSubsectionNumberRef.current = question.subSectionNumber
        })
    }

    const validateSubsectionNavigation = () => {
        setCanGoToNextSubsection(currentSubsectionNumber < maxSubsectionNumberRef.current)
        setCanGoToPreviousSubsection(currentSubsectionNumber > 1)

        let totalSectionQuestions: number
        const questionSet = questions.find(value => value.subSectionNumber === currentSubsectionNumber)

        if ((questionSet as QuestionGroup)?.questions)
            totalSectionQuestions = (questionSet as QuestionGroup).questions.length
        else
            totalSectionQuestions = questions.filter(value => value.subSectionNumber === currentSubsectionNumber).length

        const totalSectionQuestionAttempts = questionAttempts?.filter(value => value.questionId === questionSet.id).length

        setCanNavigateSubsections(totalSectionQuestionAttempts === totalSectionQuestions)
    }

    useEffect(() => {
        if (questionAttempts && questionAttempts.length > answeredQuestions)
            setAnsweredQuestions(questionAttempts?.length)
    }, [questionAttempts])

    useEffect(() => {
        timeTakenRef.current = 0
        const intervalId = setInterval(() => {
            timeTakenRef.current += 1
        }, 1000)

        return () => clearInterval(intervalId)
    }, [currentSubsectionNumber])

    useEffect(() => {
        if (questions) {
            countQuestions()
            countSubsections()
        }
    }, [questions])

    useEffect(() => {
        if (questions && currentSubsectionNumber && questionAttempts)
            validateSubsectionNavigation()
    }, [questions, currentSubsectionNumber, questionAttempts])

    return <ITestRoomContext.Provider value={{
        answerQuestion: handleAnswerQuestion,
        nextSubsection: handleNextSubsection,
        previousSubsection: handlePreviousSubsection,
        questions,
        setQuestions,
        questionAttempts,
        flagQuestion: handleFlagQuestion,
        flaggedQuestions,
        submit: handleSubmit,
        submitting: submitSessionAsyncResult.loading,
        testSubmitted: submitSessionAsyncResult.isSuccess,
        timerEnabled,
        setTimerEnabled,
        sessionId,
        setSessionId,
        awaitTimer,
        setTest,
        test,
        currentSubsectionNumber,
        currentSubsectionQuestionNumber,
        clearTimeouts,
        totalQuestions,
        answeredQuestions,
        setAnsweredQuestions,
        canGoToNextSubsection,
        canGoToPreviousSubsection,
        canNavigateSubsections,
        reachedSubsectionsRef,
        cursorPositionRef,
        submitFailed: !submitSessionAsyncResult.loading && !submitSessionAsyncResult.isSuccess,
        hasRequestedSubmit: hasRequestedSubmitRef.current
    }}>
        {props.children}
    </ITestRoomContext.Provider>
}

export default TestRoomContext